// Wrapper for Google Analytics
// It's important that it does not conflict with any GA use on the host
// page when this is embedded, so we use our own custom tracker.
// Gated before analytics.js is requested, not just before ga() sends: silencing
// sends alone still hands Google the viewer's IP. Rules in ./ga-policy.
import Config from './config';
import { isOptedOut, resolveAnalyticsPolicy } from './ga-policy';

const IS_PRODUCTION_BUILD = process.env.NODE_ENV == 'production';

var tracker = null;

function currentPolicy() {
  return resolveAnalyticsPolicy({
    hostname: (window.location && window.location.hostname) || '',
    isProductionBuild: IS_PRODUCTION_BUILD,
    pageOptOut: isOptedOut(window.BOXCAST_DISABLE_ANALYTICS),
    configOptOut: isOptedOut(Config.get('disableAnalytics')),
  });
}

function injectAnalyticsJs(trackerName) {
  (function (i, s, o, g, r, a, m) {
    i['GoogleAnalyticsObject'] = r;
    (i[r] =
      i[r] ||
      function () {
        (i[r].q = i[r].q || []).push(arguments);
      }),
      (i[r].l = 1 * new Date());
    (a = s.createElement(o)), (m = s.getElementsByTagName(o)[0]);
    a.async = 1;
    a.src = g;
    m.parentNode.insertBefore(a, m);
  })(window, document, 'script', 'https://www.google-analytics.com/analytics.js', trackerName);
}

function ensureTracker() {
  if (tracker !== null) {
    return tracker;
  }

  const policy = currentPolicy();
  if (!policy.enabled) {
    tracker = false;
    return tracker;
  }

  const trackerName = Config.get('gaTrackerName');
  injectAnalyticsJs(trackerName);

  const send = (...args) => window[trackerName](...args);
  send('create', Config.get('gaTrackerCode'), 'auto');
  send('require', 'displayfeatures');
  send('set', 'hostname', location.hostname);
  send('set', 'dimension1', Config.get('version'));
  send('send', 'pageview');

  tracker = send;
  return tracker;
}

export function ga(...args) {
  const send = ensureTracker();
  if (!send) {
    if (!IS_PRODUCTION_BUILD) {
      console.log('[ga] swallowing event');
    }
    return;
  }
  send(...args);
}

export function analyticsStatus() {
  const policy = currentPolicy();
  return {
    enabled: policy.enabled,
    reason: policy.reason,
    hostname: policy.hostname,
    matchedDomain: policy.matchedDomain,
    loaded: typeof tracker === 'function',
    trackerName: Config.get('gaTrackerName'),
    version: Config.get('version'),
  };
}

export function gaEvent(tag, args, category = 'load') {
  ga('send', 'event', tag, category, JSON.stringify(args));
}

export function gaSafely(tag, args, fn) {
  try {
    var result = fn();
    gaEvent(tag, args);
    return result;
  } catch (err) {
    if (err.message != 'halt') {
      var error = `${err.message} ${err.filename}:${err.lineNumber}`;
      console.error(error);
      ga('send', 'event', tag, 'fatal', error);
      ga('send', 'exception', { exFatal: true, exDescription: error });
    }
    throw err;
  }
}

export function gaServerError(obj, resp, options) {
  resp = resp || {};
  obj = obj || {};
  var error = `${resp.status} ${resp.responseText}`;
  var label = `${JSON.stringify([obj.url(), options.data])} -> ${error}`;
  var action = 'error';
  if ([401, 402, 403].indexOf(resp.status) >= 0) {
    action = 'warn';
  }
  ga('send', 'event', 'server', action, label, { nonInteraction: 1 });
}
